import React, { useState, useEffect } from "react";
import { getData, postData } from "utils/Gateway";
import * as url from "helper/UrlHelper";

import { vitalsOptions as STATIC_VITALS } from "data/medical/vitals";

const PatientAskConsentPopup = ({
  isOpen = false,
  onClose = () => {},
  patientObj
}) => {

  const token = localStorage.getItem("token");

  const [activeTab, setActiveTab] = useState("total");
  const [duration, setDuration] = useState("1"); // hours by default as string
  const [customDuration, setCustomDuration] = useState("");
  const [tillDate, setTillDate] = useState(""); // ISO-local datetime input value (yyyy-MM-ddTHH:mm)
  const [pendingResponse, setPendingResponse] = useState(false);

  const patientId = patientObj?._id?? null;

  // Accordion open state
  const [partialOpen, setPartialOpen] = useState({
    treatments: true,
    labReports: true,
    prescriptions: true,
    imaging: true,
    medicines: true,
    appointments: true,
    surgery: true,
    vitals: true,
  });

  // Per-section option states (default empty arrays)
  const [treatmentsOptions, setTreatmentsOptions] = useState([]);
  const [labReportsOptions, setLabReportsOptions] = useState([]);
  const [prescriptionsOptions, setPrescriptionsOptions] = useState([]);
  const [imagingOptionsState, setImagingOptionsState] = useState([]);
  const [medicinesOptions, setMedicinesOptions] = useState([]);
  const [appointmentsOptions, setAppointmentsOptions] = useState([]);
  const [surgeryOptions, setSurgeryOptions] = useState([]);
  const [vitalsOptionsState, setVitalsOptionsState] = useState(STATIC_VITALS);

  // Normalize API items to { value, label }
  const normalizeOptions = (items) => {
    if (!items || !Array.isArray(items)) return [];
    return items
      .map((it) => {
        if (!it) return null;
        if (typeof it === "string") return { value: it, label: it };
        if (it.value && it.label) return it;
        if (it.id || it._id) return { value: it.value || it.id || it._id, label: it.label || it.name || it.title || String(it.value || it.id || it._id) };
        // fallback
        return { value: it.toString(), label: it.toString() };
      })
      .filter(Boolean);
  };

  // Selections for each section (explicit keys)
  const [partialSelections, setPartialSelections] = useState({
    treatments: [],
    labReports: [],
    prescriptions: [],
    imaging: [],
    medicines: [],
    appointments: [],
    surgery: [],
    vitals: [],
  });

  const toggleAccordion = (section) => {
    setPartialOpen((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const togglePartialCheckbox = (section, optionValue) => {
    setPartialSelections((prev) => {
      const list = prev[section] || [];
      if (list.includes(optionValue)) {
        return { ...prev, [section]: list.filter((v) => v !== optionValue) };
      }
      return { ...prev, [section]: [...list, optionValue] };
    });
  };

  // --- ADDED: helpers to get option values per section and select/unselect all ---
  const getOptionValuesForSection = (section) => {
    switch (section) {
      case "treatments":
        return (treatmentsOptions || []).map((opt) => opt._id);
      case "labReports":
        return (labReportsOptions || []).map((opt) => opt.value ?? opt._id ?? opt.id);
      case "prescriptions":
        return (prescriptionsOptions || []).map((opt) => opt.value ?? opt._id ?? opt.id);
      case "imaging":
        return (imagingOptionsState || []).map((opt) => opt.value ?? opt._id ?? opt.id);
      case "medicines":
        return (medicinesOptions || []).map((opt) => opt.value ?? opt._id ?? opt.id);
      case "appointments":
        return (appointmentsOptions || []).map((opt) => opt.value ?? opt._id ?? opt.id);
      case "surgery":
        return (surgeryOptions || []).map((opt) => opt.value ?? opt._id ?? opt.id);
      case "vitals":
        return (vitalsOptionsState || []).map((opt) => opt.value ?? opt._id ?? opt.id);
      default:
        return [];
    }
  };

  // Count helper: returns number of records for a section
  const getCountForSection = (section) => {
    const values = getOptionValuesForSection(section);
    return Array.isArray(values) ? values.length : 0;
  };
  
  const isAllSelectedForSection = (section) => {
    const opts = getOptionValuesForSection(section);
    const sel = partialSelections[section] || [];
    return opts.length > 0 && sel.length === opts.length;
  };

  const toggleSelectAllForSection = (section, checked) => {
    const vals = getOptionValuesForSection(section);
    setPartialSelections((prev) => ({
      ...prev,
      [section]: checked ? vals : [],
    }));
  };
  // --- END ADDED ---
  
  useEffect(() => {
    if (!isOpen) {
      // reset when closed
      setActiveTab("total");
      setDuration("1");
      setCustomDuration("");
      setTillDate("");
      // reset partial selections and accordion
      setPartialOpen({
        treatments: true,
        labReports: false,
        prescriptions: false,
        imaging: false,
        medicines: false,
        appointments: false,
        surgery: false,
        vitals: false,
      });
      setPartialSelections({
        treatments: [],
        labReports: [],
        prescriptions: [],
        imaging: [],
        medicines: [],
        appointments: [],
        surgery: [],
        vitals: [],
      });
      // clear fetched options when closed (explicit)
      setTreatmentsOptions([]);
      setLabReportsOptions([]);
      setPrescriptionsOptions([]);
      setImagingOptionsState([]);
      setMedicinesOptions([]);
      setAppointmentsOptions([]);
      setSurgeryOptions([]);
      // keep static vitals available
      setVitalsOptionsState(STATIC_VITALS);
    }
  }, [isOpen]);

  // Auto-select all items when switching to total or partial tab
  useEffect(() => {
    if (isOpen && (activeTab === "total" || activeTab === "partial")) {
      // Select all items in all sections
      const allSelections = {
        treatments: (treatmentsOptions || []).map((opt) => opt._id),
        labReports: (labReportsOptions || []).map((opt) => opt._id),
        prescriptions: (prescriptionsOptions || []).map((opt) => opt._id),
        imaging: (imagingOptionsState || []).map((opt) => opt.value ?? opt._id ?? opt.id),
        medicines: (medicinesOptions || []).map((opt) => opt._id),
        appointments: (appointmentsOptions || []).map((opt) => opt._id),
        surgery: (surgeryOptions || []).map((opt) => opt.value ?? opt._id ?? opt.id),
        vitals: (vitalsOptionsState || []).map((opt) => opt.value),
      };
      
      setPartialSelections(allSelections);

      // If partial tab, open all accordions
      if (activeTab === "partial") {
        setPartialOpen({
          treatments: true,
          labReports: true,
          prescriptions: true,
          imaging: true,
          medicines: true,
          appointments: true,
          surgery: true,
          vitals: true,
        });
      }
    }
  }, [activeTab, isOpen, treatmentsOptions, labReportsOptions, prescriptionsOptions, imagingOptionsState, medicinesOptions, appointmentsOptions, surgeryOptions, vitalsOptionsState]);

  const fetchSection = async (section, setter) => {
        if (section) {
            try {
                let requestUrl = url.API_BASE_URL;
                
                switch(section) {
                    case "treatments":
                        requestUrl += url.API_GET_ALL_TREATMENTS + `?token=${token}&patientid=${patientObj._id}`;
                        break;
                    case "labReports":
                        requestUrl += url.API_GET_ALL_MEDICAL_TEST + `?token=${token}&members=${patientObj._id}`;
                        break;
                    case "prescriptions":
                        requestUrl += url.API_GET_ALL_MEDICAL_DOCUMENT + `?token=${token}&members=${patientObj._id}`;
                        break;
                    case "medicines":
                        requestUrl += url.API_GET_ALL_MEDICINE + `?token=${token}&members=${patientObj._id}`;
                        break;
                    case "appointments":
                        requestUrl += url.API_GET_ALL_AGENDA + `?token=${token}&associateduser=${patientObj._id}&issent=true`;
                        break;
                    // case "labReports":
                }   

                console.log("Fetching url", section, requestUrl);
                const resp = await getData(requestUrl);
                console.log("Response received:", section, resp);

                if (resp && resp.status && resp.data) {
                    setter(resp.data);
                    return;
                }

            } catch (err) {
                setter([]);
                return;
            }
        }

    };

  // Fetch section data when offcanvas opens (explicit per-section fetch)
  useEffect(() => {

    if (isOpen === false) return;

    console.log("isOpen state changed:", isOpen);

    console.log("Patient ID changed, fetching section data...", patientId);

    // call each fetch explicitly (no loop)
    fetchSection("treatments", setTreatmentsOptions);
    fetchSection("labReports", setLabReportsOptions);
    fetchSection("prescriptions", setPrescriptionsOptions);
    fetchSection("medicines", setMedicinesOptions);
    fetchSection("appointments", setAppointmentsOptions);
  }, [patientId, isOpen]);

  const durationOptions = [
    { label: "1 Day", value: "1" },
    { label: "1 Week", value: "7" },
    { label: "1 Month", value: "30" },
    { label: "Custom Date", value: "till_date" },
    { label: "Permanent", value: "1000" },
  ];

  const handleSelectDuration = (val) => {
    setDuration(val);
    if (val !== "till_date") setTillDate("");
    // keep backward compatibility for customDuration if used elsewhere
    if (val !== "custom") setCustomDuration("");
  };

  const handleSend = async () => {
    const payload = {
      type: activeTab === "total" ? "total" : "partial",
      duration,
      // include ISO timestamp when till_date selected
      tillDate: duration === "till_date" && tillDate ? new Date(tillDate).toISOString() : undefined,
      patientId,
      partialSelections: partialSelections, // if total, send all selections as all will be selected by default
    };
    
    console.log("Sending consent request payload:", payload);

    setPendingResponse(true);
    try {
      const requestUrl = url.API_BASE_URL + url.API_MEDICAL_CONSENT_REQUEST_ACCESS + `?token=${token}`;
      const response = await postData(requestUrl, payload);
      console.log("Consent request response:", response);
      // handle successful response if needed
      if (response && response.status) {
        // close the offcanvas using Bootstrap API
          const offcanvasElement = document.getElementById('patientAskConsentOffCanvas');
          if (offcanvasElement) {
              const bsOffcanvas = window.bootstrap.Offcanvas.getInstance(offcanvasElement);
              if (bsOffcanvas) {
                  bsOffcanvas.hide();
              }
          }
          onClose();
      }
    } catch (error) {
      console.error("Error sending consent request:", error);
    } finally {
      setPendingResponse(false);
    }
  };

  return (
    <div
      className="offcanvas lg offcanvas-end bg-white border-0 shadow-lg"
      tabIndex="-1"
      id="patientAskConsentOffCanvas"
      aria-labelledby="patientAskConsentOffCanvasLabel"
    >
      <div className="offcanvas-header bg-primary text-white p-4 pb-3 border-bottom">
        <div className="d-flex align-items-center gap-3">
          <div className="icon-wrapper p-3 bg-white rounded-circle d-flex align-items-center justify-content-center">
            <span className="material-symbols-outlined text-primary fs-5 icon-md">assignment_ind</span>
          </div>
          <div>
            <h3 className="offcanvas-title fw-bold text-white mb-0">Request Patient Consent</h3>
            <p className="text-white fs-sm mb-0">Request access to {patientObj?.name} medical records</p>
          </div>
        </div>
        <button
          type="button"
          className="btn-close p-2 bg-gray-200 rounded-circle shadow-sm border-0"
          data-bs-dismiss="offcanvas"
          aria-label="Close"
          //onClick={onClose}
        ></button>
      </div>

      <div className="offcanvas-body p-4" style={{ backgroundColor: "#f8f9fa", minHeight: "320px" }}>
        {/* Tabs - button group for clearer active state with primary color */}
        <div className="d-flex align-items-center justify-content-start mb-3" role="tablist" aria-label="Consent tabs">
          <div className="btn-group" role="group" aria-label="Consent type">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "total"}
              className={activeTab === "total" ? "btn btn-primary btn-sm" : "btn btn-outline-secondary btn-sm"}
              onClick={() => setActiveTab("total")}
            >
              Total Consent
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "partial"}
              className={activeTab === "partial" ? "btn btn-primary btn-sm" : "btn btn-outline-secondary btn-sm"}
              onClick={() => setActiveTab("partial")}
            >
              Partial Consent
            </button>
          </div>
          <small className="text-muted ms-3 d-none d-md-inline">Choose Full or Selective access. Active tab highlighted.</small>
        </div>

        {/* Tab content */}
        <div>
          {activeTab === "total" && (
            <div>
              <div className="p-3 rounded-3 mb-3" style={{ backgroundColor: "#fff4f0", border: "1px solid #ffd8cc" }}>
                <div className="d-flex align-items-start gap-3">
                  <div style={{ fontSize: 28, color: "#f97316" }}>
                    <span className="material-symbols-outlined">warning</span>
                  </div>
                  <div>
                    <h6 className="mb-1" style={{ color: "#c2410c" }}>Request Total Consent</h6>
                    <p className="mb-0 text-muted">You are requesting full access to {patientObj?.name} medical records</p>
                  </div>
                </div>
              </div>

              <div className="card p-3 mb-3">
                {/* Summary counts for all sections (Total tab) */}
                <div className="mb-2">
                  <label className="d-block fw-semibold mb-1">Records available</label>
                  <div className="d-flex gap-2 flex-wrap">
                    <span className="badge bg-light text-muted">Treatments ({getCountForSection("treatments")})</span>
                    <span className="badge bg-light text-muted">Lab Reports ({getCountForSection("labReports")})</span>
                    <span className="badge bg-light text-muted">Prescriptions ({getCountForSection("prescriptions")})</span>
                    <span className="badge bg-light text-muted">Medicines ({getCountForSection("medicines")})</span>
                    <span className="badge bg-light text-muted">Appointments ({getCountForSection("appointments")})</span>
                    <span className="badge bg-light text-muted">Vitals ({getCountForSection("vitals")})</span>
                  </div>
                </div>

                <div className="mt-3">
                  <label className="d-block fw-semibold mb-2">Duration Selection</label>
                  <div className="d-flex gap-2 flex-wrap">
                    {durationOptions.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        className={`btn btn-outline-secondary btn-sm ${duration === opt.value ? "active btn-primary text-white" : ""}`}
                        onClick={() => handleSelectDuration(opt.value)}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  {duration === "till_date" && (
                    <div className="mt-2">
                      <input
                        type="datetime-local"
                        className="form-control form-control-sm"
                        value={tillDate}
                        onChange={(e) => setTillDate(e.target.value)}
                      />
                      <small className="text-muted">Select the end date & time until which access should be granted.</small>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === "partial" && (
            <div>
              <div className="p-3 rounded-3 mb-3" style={{ backgroundColor: "#eef9ff", border: "1px solid #cfeeff" }}>
                <div className="d-flex align-items-start gap-3">
                  <div style={{ fontSize: 28, color: "#0ea5e9" }}>
                    <span className="material-symbols-outlined">description</span>
                  </div>
                  <div>
                    <h6 className="mb-1" style={{ color: "#0369a1" }}>Request Partial Consent</h6>
                    <p className="mb-0 text-muted">Choose the medical records you need. The patient will review and approve the request.</p>
                  </div>
                </div>
              </div>

              {/* Duration Selection (same as Total Consent) */}
              <div className="card p-3 mb-3">
                <label className="d-block fw-semibold mb-2">Duration Selection</label>
                <div className="d-flex gap-2 flex-wrap">
                  {durationOptions.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      className={`btn btn-outline-secondary btn-sm ${duration === opt.value ? "active btn-primary text-white" : ""}`}
                      onClick={() => handleSelectDuration(opt.value)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                {duration === "till_date" && (
                  <div className="mt-2">
                    <input
                      type="datetime-local"
                      className="form-control form-control-sm"
                      value={tillDate}
                      onChange={(e) => setTillDate(e.target.value)}
                    />
                    <small className="text-muted">Select the end date & time until which access should be granted.</small>
                  </div>
                )}
              </div>

             {/* consent of patient records */}
              <div className="mb-3">
                {/* Treatments */}
                <div className="mb-2 rounded-3 overflow-hidden" style={{ background: "#fff", border: "1px solid #e9ecef" }}>
                  <button
                    type="button"
                    className="d-flex justify-content-between align-items-center w-100 p-3 bg-transparent border-0"
                    onClick={() => toggleAccordion("treatments")}
                    aria-expanded={!!partialOpen.treatments}
                  >
                    <div className="d-flex align-items-center">
                      <input
                        type="checkbox"
                        className="form-check-input me-2"
                        aria-label="Select all treatments"
                        checked={isAllSelectedForSection("treatments")}
                        disabled={(treatmentsOptions || []).length === 0}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          e.stopPropagation();
                          toggleSelectAllForSection("treatments", e.target.checked);
                        }}
                      />
                      <strong>
                        Treatments <small className="text-muted">({getCountForSection("treatments")})</small>
                      </strong>
                    </div>
                    <span className="material-symbols-outlined">{partialOpen.treatments ? "expand_less" : "expand_more"}</span>
                  </button>
                  {partialOpen.treatments && (
                    <div className="p-3 border-top" style={{ background: "#f8f9fa" }}>
                      {treatmentsOptions.length === 0 ? (
                        <div className="text-muted fs-sm">Not enough records to share.</div>
                      ) : (
                        treatmentsOptions.map((opt) => {
                          const val = opt._id;
                          return (
                            <div className="form-check mb-2" key={val}>
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id={`treatments-${val}`}
                                checked={partialSelections.treatments.includes(val)}
                                onChange={() => togglePartialCheckbox("treatments", val)}
                              />
                              <label className="form-check-label ms-2" htmlFor={`treatments-${val}`}>{opt.title}</label>
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>

                {/* Lab Reports */}
                <div className="mb-2 rounded-3 overflow-hidden" style={{ background: "#fff", border: "1px solid #e9ecef" }}>
                  <button
                    type="button"
                    className="d-flex justify-content-between align-items-center w-100 p-3 bg-transparent border-0"
                    onClick={() => toggleAccordion("labReports")}
                    aria-expanded={!!partialOpen.labReports}
                  >
                    <div className="d-flex align-items-center">
                      <input
                        type="checkbox"
                        className="form-check-input me-2"
                        aria-label="Select all lab reports"
                        checked={isAllSelectedForSection("labReports")}
                        disabled={(labReportsOptions || []).length === 0}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          e.stopPropagation();
                          toggleSelectAllForSection("labReports", e.target.checked);
                        }}
                      />
                      <strong>
                        Lab Reports <small className="text-muted">({getCountForSection("labReports")})</small>
                      </strong>
                    </div>
                    <span className="material-symbols-outlined">{partialOpen.labReports ? "expand_less" : "expand_more"}</span>
                  </button>
                  {partialOpen.labReports && (
                    <div className="p-3 border-top" style={{ background: "#f8f9fa" }}>
                      {labReportsOptions.length === 0 ? (
                        <div className="text-muted fs-sm">Not enough records to share.</div>
                      ) : (
                        labReportsOptions.map((opt) => {
                          const val = opt._id;
                          return (
                            <div className="form-check mb-2" key={val}>
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id={`labReports-${val}`}
                                checked={partialSelections.labReports.includes(val)}
                                onChange={() => togglePartialCheckbox("labReports", val)}
                              />
                              <label className="form-check-label ms-2" htmlFor={`labReports-${val}`}>
                                {opt.name}
                                {opt.code && <span style={{ fontSize: '0.85em', color: '#6c757d', marginLeft: '6px', fontWeight: '400' }}>Code: {opt.code}</span>}
                              </label>
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>

                {/* Prescriptions */}
                <div className="mb-2 rounded-3 overflow-hidden" style={{ background: "#fff", border: "1px solid #e9ecef" }}>
                  <button
                    type="button"
                    className="d-flex justify-content-between align-items-center w-100 p-3 bg-transparent border-0"
                    onClick={() => toggleAccordion("prescriptions")}
                    aria-expanded={!!partialOpen.prescriptions}
                  >
                    <div className="d-flex align-items-center">
                      <input
                        type="checkbox"
                        className="form-check-input me-2"
                        aria-label="Select all prescriptions"
                        checked={isAllSelectedForSection("prescriptions")}
                        disabled={(prescriptionsOptions || []).length === 0}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          e.stopPropagation();
                          toggleSelectAllForSection("prescriptions", e.target.checked);
                        }}
                      />
                      <strong>
                        Prescriptions <small className="text-muted">({getCountForSection("prescriptions")})</small>
                      </strong>
                    </div>
                    <span className="material-symbols-outlined">{partialOpen.prescriptions ? "expand_less" : "expand_more"}</span>
                  </button>
                  {partialOpen.prescriptions && (
                    <div className="p-3 border-top" style={{ background: "#f8f9fa" }}>
                      {prescriptionsOptions.length === 0 ? (
                        <div className="text-muted fs-sm">Not enough records to share.</div>
                      ) : (
                        prescriptionsOptions.map((opt) => {
                          const val = opt._id;
                          return (
                            <div className="form-check mb-2" key={val}>
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id={`prescriptions-${val}`}
                                checked={partialSelections.prescriptions.includes(val)}
                                onChange={() => togglePartialCheckbox("prescriptions", val)}
                              />
                              <label className="form-check-label ms-2" htmlFor={`prescriptions-${val}`}>
                                {opt.name}
                                {opt.doctorname && <span style={{ fontSize: '0.85em', color: '#6c757d', marginLeft: '6px', fontWeight: '400' }}>Doctor: {opt.doctorname}</span>}
                              </label>
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>

                {/* Medicines */}
                <div className="mb-2 rounded-3 overflow-hidden" style={{ background: "#fff", border: "1px solid #e9ecef" }}>
                  <button
                    type="button"
                    className="d-flex justify-content-between align-items-center w-100 p-3 bg-transparent border-0"
                    onClick={() => toggleAccordion("medicines")}
                    aria-expanded={!!partialOpen.medicines}
                  >
                    <div className="d-flex align-items-center">
                      <input
                        type="checkbox"
                        className="form-check-input me-2"
                        aria-label="Select all medicines"
                        checked={isAllSelectedForSection("medicines")}
                        disabled={(medicinesOptions || []).length === 0}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          e.stopPropagation();
                          toggleSelectAllForSection("medicines", e.target.checked);
                        }}
                      />
                      <strong>
                        Medicines <small className="text-muted">({getCountForSection("medicines")})</small>
                      </strong>
                    </div>
                    <span className="material-symbols-outlined">{partialOpen.medicines ? "expand_less" : "expand_more"}</span>
                  </button>
                  {partialOpen.medicines && (
                    <div className="p-3 border-top" style={{ background: "#f8f9fa" }}>
                      {medicinesOptions.length === 0 ? (
                        <div className="text-muted fs-sm">Not enough records to share.</div>
                      ) : (
                        medicinesOptions.map((opt) => {
                          const val = opt._id;
                          return (
                            <div className="form-check mb-2" key={val}>
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id={`medicines-${val}`}
                                checked={partialSelections.medicines.includes(val)}
                                onChange={() => togglePartialCheckbox("medicines", val)}
                              />
                              <label className="form-check-label ms-2" htmlFor={`medicines-${val}`}>{opt.productname}</label>
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>

                {/* Appointments */}
                <div className="mb-2 rounded-3 overflow-hidden" style={{ background: "#fff", border: "1px solid #e9ecef" }}>
                  <button
                    type="button"
                    className="d-flex justify-content-between align-items-center w-100 p-3 bg-transparent border-0"
                    onClick={() => toggleAccordion("appointments")}
                    aria-expanded={!!partialOpen.appointments}
                  >
                    <div className="d-flex align-items-center">
                      <input
                        type="checkbox"
                        className="form-check-input me-2"
                        aria-label="Select all appointments"
                        checked={isAllSelectedForSection("appointments")}
                        disabled={(appointmentsOptions || []).length === 0}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          e.stopPropagation();
                          toggleSelectAllForSection("appointments", e.target.checked);
                        }}
                      />
                      <strong>
                        Appointments <small className="text-muted">({getCountForSection("appointments")})</small>
                      </strong>
                    </div>
                    <span className="material-symbols-outlined">{partialOpen.appointments ? "expand_less" : "expand_more"}</span>
                  </button>
                  {partialOpen.appointments && (
                    <div className="p-3 border-top" style={{ background: "#f8f9fa" }}>
                      {appointmentsOptions.length === 0 ? (
                        <div className="text-muted fs-sm">Not enough records to share.</div>
                      ) : (
                        appointmentsOptions.map((opt) => {
                          const val = opt._id;
                          return (
                            <div className="form-check mb-2" key={val}>
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id={`appointments-${val}`}
                                checked={partialSelections.appointments.includes(val)}
                                onChange={() => togglePartialCheckbox("appointments", val)}
                              />
                              <label className="form-check-label ms-2" htmlFor={`appointments-${val}`}>
                                {opt.description}
                                {opt.agendadate && <span style={{ fontSize: '0.85em', color: '#6c757d', marginLeft: '6px', fontWeight: '400' }}>{opt.agendadate}</span>}
                              </label>
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>

                {/* Imaging */}
                {/* <div className="mb-2 rounded-3 overflow-hidden" style={{ background: "#fff", border: "1px solid #e9ecef" }}>
                  <button
                    type="button"
                    className="d-flex justify-content-between align-items-center w-100 p-3 bg-transparent border-0"
                    onClick={() => toggleAccordion("imaging")}
                    aria-expanded={!!partialOpen.imaging}
                  >
                    <div className="d-flex align-items-center">
                      <input
                        type="checkbox"
                        className="form-check-input me-2"
                        aria-label="Select all imaging"
                        checked={isAllSelectedForSection("imaging")}
                        disabled={(imagingOptionsState || []).length === 0}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          e.stopPropagation();
                          toggleSelectAllForSection("imaging", e.target.checked);
                        }}
                      />
                      <strong>Imaging Scans</strong>
                    </div>
                    <span className="material-symbols-outlined">{partialOpen.imaging ? "expand_less" : "expand_more"}</span>
                  </button>
                  {partialOpen.imaging && (
                    <div className="p-3 border-top" style={{ background: "#f8f9fa" }}>
                      {imagingOptionsState.length === 0 ? (
                        <div className="text-muted fs-sm">Not enough records to share.</div>
                      ) : (
                        imagingOptionsState.map((opt) => {
                          const val = opt.value;
                          return (
                            <div className="form-check mb-2" key={val}>
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id={`imaging-${val}`}
                                checked={partialSelections.imaging.includes(val)}
                                onChange={() => togglePartialCheckbox("imaging", val)}
                              />
                              <label className="form-check-label" htmlFor={`imaging-${val}`}>{opt.label}</label>
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </div> */}

                

                {/* Surgery */}
                {/* <div className="mb-2 rounded-3 overflow-hidden" style={{ background: "#fff", border: "1px solid #e9ecef" }}>
                  <button
                    type="button"
                    className="d-flex justify-content-between align-items-center w-100 p-3 bg-transparent border-0"
                    onClick={() => toggleAccordion("surgery")}
                    aria-expanded={!!partialOpen.surgery}
                  >
                    <div className="d-flex align-items-center">
                      <input
                        type="checkbox"
                        className="form-check-input me-2"
                        aria-label="Select all surgery records"
                        checked={isAllSelectedForSection("surgery")}
                        disabled={(surgeryOptions || []).length === 0}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          e.stopPropagation();
                          toggleSelectAllForSection("surgery", e.target.checked);
                        }}
                      />
                      <strong>Surgery Records</strong>
                    </div>
                    <span className="material-symbols-outlined">{partialOpen.surgery ? "expand_less" : "expand_more"}</span>
                  </button>
                  {partialOpen.surgery && (
                    <div className="p-3 border-top" style={{ background: "#f8f9fa" }}>
                      {surgeryOptions.length === 0 ? (
                        <div className="text-muted fs-sm">Not enough records to share.</div>
                      ) : (
                        surgeryOptions.map((opt) => {
                          const val = opt.value;
                          return (
                            <div className="form-check mb-2" key={val}>
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id={`surgery-${val}`}
                                checked={partialSelections.surgery.includes(val)}
                                onChange={() => togglePartialCheckbox("surgery", val)}
                              />
                              <label className="form-check-label" htmlFor={`surgery-${val}`}>{opt.label}</label>
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </div> */}

                {/* Vitals */}
                <div className="mb-2 rounded-3 overflow-hidden" style={{ background: "#fff", border: "1px solid #e9ecef" }}>
                  <button
                    type="button"
                    className="d-flex justify-content-between align-items-center w-100 p-3 bg-transparent border-0"
                    onClick={() => toggleAccordion("vitals")}
                    aria-expanded={!!partialOpen.vitals}
                  >
                    <div className="d-flex align-items-center">
                      <input
                        type="checkbox"
                        className="form-check-input me-2"
                        aria-label="Select all vitals"
                        checked={isAllSelectedForSection("vitals")}
                        disabled={(vitalsOptionsState || []).length === 0}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          e.stopPropagation();
                          toggleSelectAllForSection("vitals", e.target.checked);
                        }}
                      />
                      <strong>
                        Vitals <small className="text-muted">({getCountForSection("vitals")})</small>
                      </strong>
                    </div>
                    <span className="material-symbols-outlined">{partialOpen.vitals ? "expand_less" : "expand_more"}</span>
                  </button>
                  {partialOpen.vitals && (
                    <div className="p-3 border-top" style={{ background: "#f8f9fa" }}>
                      {vitalsOptionsState.length === 0 ? (
                        <div className="text-muted fs-sm">Not enough records to share.</div>
                      ) : (
                        vitalsOptionsState.map((opt) => {
                          const val = opt.value;
                          return (
                            <div className="form-check mb-2" key={val}>
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id={`vitals-${val}`}
                                checked={partialSelections.vitals.includes(val)}
                                onChange={() => togglePartialCheckbox("vitals", val)}
                              />
                              <label className="form-check-label ms-2" htmlFor={`vitals-${val}`}>{opt.label}</label>
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              </div>

              
            </div>
          )}
        </div>
      </div>

      <div className="offcanvas-footer p-3 border-top bg-white d-flex justify-content-between">
        <button className="btn btn-outline-secondary" data-bs-dismiss="offcanvas" onClick={onClose}>Close</button>
        <button
          className="btn btn-primary"
          onClick={handleSend}
          disabled={pendingResponse || (duration === "till_date" && !tillDate)}
        >
          {pendingResponse ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden></span>
              Sending...
            </>
          ) : (
            "Send Request"
          )}
        </button>
      </div>
    </div>
  );
};

export default PatientAskConsentPopup;
